import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

const InspectionsSummary = ({ inspections = [], inspectionsLoading = false, formatDate = (d) => d }) => {
    // Helper to get latest inspection by type
    const latestByType = (type) => {
        const items = inspections
            .filter(i => {
                // inspection_type may be present or inferred
                return (i.inspection_type && i.inspection_type.toLowerCase() === type.toLowerCase()) || (i.type && i.type.toLowerCase() === type.toLowerCase());
            })
            .slice()
            .sort((a, b) => new Date(b.inspection_date) - new Date(a.inspection_date));
        return items.length > 0 ? items[0] : null;
    };

    const addMonths = (dateStr, months) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (isNaN(d)) return null;
        const nd = new Date(d);
        nd.setMonth(nd.getMonth() + months);
        return nd;
    };

    const internal = latestByType('internal');
    const government = latestByType('government');

    // derive plate if available to build links
    const plate = (inspections && inspections.length > 0 && inspections[0].plate_no) ? inspections[0].plate_no : null;

    // Prefer explicit next_inspection_due or expiry_date from the inspection record if present,
    // otherwise fall back to computed intervals (internal: +6 months, government: +12 months)
    const internalNextDue = internal ? (internal.next_inspection_due || internal.expiry_date || addMonths(internal.inspection_date, 6)) : null;
    const governmentNextDue = government ? (government.next_inspection_due || government.expiry_date || addMonths(government.inspection_date, 12)) : null;

    const daysUntil = (date) => {
        if (!date) return null;
        const now = new Date();
        const d = new Date(date);
        if (isNaN(d)) return null;
        const diffMs = d.setHours(0, 0, 0, 0) - now.setHours(0, 0, 0, 0);
        return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    };

    const statusBadgeClass = (status) => {
        if (!status) return 'bg-gray-100 text-gray-800 border-gray-200';
        const s = String(status).toLowerCase();
        if (s.includes('pass') || s.includes('passed')) return 'bg-green-100 text-green-800 border-green-300';
        if (s.includes('fail') || s.includes('failed')) return 'bg-red-100 text-red-800 border-red-300';
        if (s.includes('conditional') || s.includes('cond')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
        return 'bg-gray-100 text-gray-800 border-gray-200';
    };

    const DuePill = ({ nextDue }) => {
        if (!nextDue) return <span className="text-xs text-gray-400">No due date</span>;
        const days = daysUntil(nextDue);
        let pillClass = 'bg-green-100 text-green-700';
        if (days !== null && days < 0) pillClass = 'bg-red-100 text-red-700';
        else if (days !== null && days <= 7) pillClass = 'bg-red-100 text-red-700';
        else if (days !== null && days <= 30) pillClass = 'bg-orange-100 text-orange-700';
        const label = days === null ? formatDate(nextDue)
            : days < 0 ? `Overdue by ${Math.abs(days)}d — ${formatDate(nextDue)}`
            : days === 0 ? `Due today — ${formatDate(nextDue)}`
            : `Due in ${days}d — ${formatDate(nextDue)}`;
        return (
            <span className={`block w-full px-2 py-0.5 rounded text-xs font-semibold ${pillClass}`}>{label}</span>
        );
    };

    const InspectionSection = ({ label, record, nextDue, viewLink }) => (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</span>
                <Link to={viewLink} className="text-xs text-blue-600 hover:underline">View all →</Link>
            </div>
            <div className="bg-white/60 rounded px-2 py-1.5 space-y-1.5">
                <DuePill nextDue={nextDue} />
                {record ? (
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-gray-500 shrink-0">{formatDate(record.inspection_date)}</span>
                            <span className={`px-1.5 py-0.5 text-xs font-semibold rounded border shrink-0 ${statusBadgeClass(record.result || record.inspection_status || record.status)}`}>
                                {record.result || record.inspection_status || record.status || 'N/A'}
                            </span>
                        </div>
                        {(record.certificate_link || record.inspection_report_link) && (
                            <a className="text-xs text-blue-600 hover:underline shrink-0" href={record.certificate_link || record.inspection_report_link} target="_blank" rel="noreferrer noopener">Cert</a>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">No record found</span>
                        <Link to={viewLink} className="text-xs text-[#FF5733] hover:underline">+ Add</Link>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="bg-gray-50 rounded-sm border border-gray-200 p-3">
            <h3 className="text-xs font-bold text-[#2a2e30] mb-2.5 flex items-center">
                <div className="w-2 h-2 bg-[#FF5733] rounded-full mr-2"></div>
                Inspections
            </h3>
            {inspectionsLoading ? (
                <div className="text-center py-2">
                    <FontAwesomeIcon icon={faSpinner} spin className="text-[#FF5733]" />
                </div>
            ) : (
                <div className="space-y-3 text-xs">
                    <InspectionSection
                        label="Internal"
                        record={internal}
                        nextDue={internalNextDue}
                        viewLink={plate ? `/inspections/internal?plate=${encodeURIComponent(plate)}` : '/inspections/internal'}
                    />
                    <div className="border-t border-gray-200" />
                    <InspectionSection
                        label="Government"
                        record={government}
                        nextDue={governmentNextDue}
                        viewLink={plate ? `/inspections/government?plate=${encodeURIComponent(plate)}` : '/inspections/government'}
                    />
                </div>
            )}
        </div>
    );
};

export default InspectionsSummary;
